import { ChangeDetectorRef, Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageBuilderAddService } from '@app/pages/page-builder/page-builder/page-builder-add/@core/page-builder-add.service';
import { IReportEmbedConfiguration, models, Report } from 'powerbi-client';
import { PowerBIEmbedModule } from 'powerbi-client-angular';
import { AccordianComponent } from '@app/shared/custom-component/accordian/accordian.component';

@Component({
  selector: 'app-admin-reports',
  standalone: true,
  imports: [CommonModule, PowerBIEmbedModule,AccordianComponent],
  templateUrl: './admin-reports.component.html',
  styleUrls: ['./admin-reports.component.scss']
})
export class AdminReportsComponent implements OnInit, OnDestroy {
  @ViewChildren(AccordianComponent) accordions!: QueryList<AccordianComponent>;

  isLoading = true;
  isReportReady = false;
  errorMessage = '';

  private tokenRefreshTimer?: ReturnType<typeof setTimeout>;
  private loadingTimeoutTimer?: ReturnType<typeof setTimeout>;

  reportConfig: IReportEmbedConfiguration = {
    type: 'report',
    embedUrl: undefined,
    tokenType: models.TokenType.Embed,
    accessToken: undefined,
    id: undefined,
    settings: {
      panes: {
        filters: { expanded: false, visible: false },
        pageNavigation: {
          visible: true,
          position: models.PageNavigationPosition.Left
        }
      },
      background: models.BackgroundType.Transparent,
      navContentPaneEnabled: true,
      filterPaneEnabled: true,
      layoutType: models.LayoutType.Custom,
      zoomLevel: 1,
      customLayout: {
        displayOption: models.DisplayOption.FitToPage
      },
      visualRenderedEvents: false
    }
  };

  reportClass = 'report-container';
  phasedEmbeddingFlag = false;
  embeddedReport!: Report;

  eventHandlers = new Map<string, (event?: any) => void>([
    [
      'loaded',
      () => {
        this.isLoading = false;
        this.clearLoadingTimeout();
        console.log('Report loaded');

        this.embeddedReport
          .getPages()
          .then(pages => {
            console.log(
              'Available pages:',
              pages.map(p => ({
                name: p.name,
                displayName: p.displayName,
                isActive: p.isActive
              }))
            );
          })
          .catch(e => console.warn('Could not fetch pages:', e));
      }
    ],
    [
      'rendered',
      () => console.log('Report rendered')
    ],
    [
      'error',
      event => {
        console.error('Embed error:', event?.detail);
        this.errorMessage = 'Failed to render the report. Check the console for details.';
        this.isLoading = false;
        this.clearLoadingTimeout();
      }
    ]
  ]);

  constructor(private pageBuilderAddService: PageBuilderAddService,private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.loadEmbedConfig();
  }

    ngAfterViewInit(): void {
    setTimeout(() => {
      this.accordions?.forEach((accordion: AccordianComponent) => {
        accordion.isOpen = false;
      });
      this.cdr.detectChanges();
    });
  }
  
  ngOnDestroy(): void {
    this.clearTokenRefreshTimer();
    this.clearLoadingTimeout();
  }

  // ─── Load Initial Config ───────────────────────────────────────────────────

  private loadEmbedConfig(): void {
    this.loadingTimeoutTimer = setTimeout(() => {
      if (this.isLoading) {
        this.isLoading = false;
        this.errorMessage = 'Report is taking too long to load. Please try again.';
      }
    }, 30000);

    this.pageBuilderAddService
      .getEmbedConfig()
      .then(cfg => {
        this.reportConfig = {
          ...this.reportConfig,
          id: cfg?.data?.reportId,
          embedUrl: cfg?.data?.embedUrl,
          accessToken: cfg?.data?.embedToken
        };

        this.isReportReady = true;
        this.scheduleTokenRefresh(cfg?.data?.tokenExpiry);
      })
      .catch(err => {
        console.error('Failed to load embed config:', err);
        this.errorMessage = 'Could not reach the backend. Is server.js running on port 3000?';
        this.isLoading = false;
        this.clearLoadingTimeout();
      });
  }

  // ─── Token Refresh ─────────────────────────────────────────────────────────

  private scheduleTokenRefresh(tokenExpiry?: string): void {
    this.clearTokenRefreshTimer();

    const expiryTime = tokenExpiry ? new Date(tokenExpiry).getTime() : NaN;
    const delay = expiryTime - Date.now() - 2 * 60 * 1000;

    if (!Number.isFinite(delay) || delay <= 0) {
      console.warn('Token refresh not scheduled — invalid or too-close expiry:', tokenExpiry);
      return;
    }

    this.tokenRefreshTimer = setTimeout(async () => {
      try {
        const cfg = await this.pageBuilderAddService.getEmbedConfig();
        await this.embeddedReport?.setAccessToken(cfg?.data?.embedToken);
        console.log('Embed token refreshed silently');
        this.scheduleTokenRefresh(cfg?.data?.tokenExpiry);
      } catch (err) {
        console.warn('Silent token refresh failed:', err);
      }
    }, delay);
  }

  private clearTokenRefreshTimer(): void {
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = undefined;
    }
  }

  private clearLoadingTimeout(): void {
    if (this.loadingTimeoutTimer) {
      clearTimeout(this.loadingTimeoutTimer);
      this.loadingTimeoutTimer = undefined;
    }
  }

  // ─── Report Event ──────────────────────────────────────────────────────────

  onReportEmit(event: Event): void {
    this.embeddedReport = event as unknown as Report;
  }
}